import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, iif, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { v4 as uuidv4 } from 'uuid';
import { AuthService } from './auth.service';
import { LIB_CONFIG } from '@/provide-lib-config';
import { isPlatformBrowser } from '@angular/common';
import { Environment } from './../../../environments/environment.interface';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private isBrowser: any = isPlatformBrowser(inject(PLATFORM_ID));

  constructor(
    protected oauthService: OAuthService,
    protected router: Router,
    protected http: HttpClient,
    protected authService: AuthService,
    @Inject(LIB_CONFIG) private config: Environment
  ) {}

  public initialiseToken(): Promise<void> {
    return new Promise<void>((resolve) => {
      return this.getAuthToken()
        .pipe(mergeMap((token) => iif(() => this.authService.isAuthenticated(), this.getRegToken(token), of(true))))
        .subscribe(() => resolve());
    });
  }

  public getAuthTokenFromStorage(): any {
    const regToken = this.getRegTokenFromStorage();
    if (regToken) {
      return regToken;
    }

    if (this.isBrowser) {
      const authToken = sessionStorage.getItem(this.getAuthTokenName());
      const expiry = sessionStorage.getItem(this.getAuthTokenExpiryName());
      const now = new Date().getTime();
      if ((authToken && expiry && parseInt(expiry, 10) > now) || (authToken && expiry === 'NaN')) {
        return authToken;
      }
    }

    return null;
  }

  public removeAuthTokenFromStorage(): void {
    this.removeRegTokenFromStorage();
    if (this.isBrowser) {
      sessionStorage.removeItem(this.getAuthTokenName());
      sessionStorage.removeItem(this.getAuthTokenExpiryName());
    }
  }

  public getAuthToken(): Observable<string> {
    const authToken = this.getAuthTokenFromStorage();
    if (authToken) {
      return of(authToken);
    }

    return this.getAuthTokenRequest().pipe(
      map((result: any) => {
        if (this.isBrowser) {
          sessionStorage.setItem(this.getAuthTokenExpiryName(), this.getTimestampFromSeconds(result?.expires_in));
          sessionStorage.setItem(this.getAuthTokenName(), result?.access_token);
        }
        return result?.access_token;
      })
    );
  }

  private getAuthTokenRequest(): Observable<any> {
    const uuid = uuidv4();
    const url = `${this.config?.apigeeApi?.baseUrl}${this.config?.compaxAngularApi?.authToken?.url}`;

    const body = new HttpParams()
      .set('client_secret', this.config?.apigeeApi?.clientSecret)
      .set('client_id', this.config?.apigeeApi?.clientId)
      .set('scope', 'all')
      .set('grant_type', 'client_credentials');

    let headers = new HttpHeaders();
    headers = headers
      .set('DAPI-AppID', this.config?.apigeeApi?.dapiAppId)
      .set('DAPI-ClientSecret', this.config?.apigeeApi?.dapiClientSecret)
      .set('DAPI-ChannelID', this.config?.apigeeApi?.dapiChannelId)
      .set('DAPI-RequestID', uuid)
      .set('DAPI-CorrelationID', uuid)
      .set('DAPI-RequestTimestamp', new Date().toISOString());
    if (this.isBrowser) {
      headers.set('ZEUS-Location', window.location.pathname);
    }

    return this.http.post(url, body, { headers });
  }

  public getRegTokenFromStorage(): any {
    if (this.isBrowser) {
      const regToken = sessionStorage.getItem(this.getRegTokenName());
      const expiry = sessionStorage.getItem(this.getRegTokenExpiryName());
      const now = new Date().getTime();
      if ((regToken && expiry && parseInt(expiry, 10) > now) || (regToken && expiry === 'NaN')) {
        return regToken;
      }
    }
    return null;
  }

  public removeRegTokenFromStorage(): void {
    if (this.isBrowser) {
      sessionStorage.removeItem(this.getRegTokenName());
      sessionStorage.removeItem(this.getRegTokenExpiryName());
    }
  }

  public getRegToken(authToken: string): Observable<any> {
    const regToken = this.getRegTokenFromStorage();
    if (regToken) {
      return of(regToken);
    }

    return this.getRegTokenRequest(authToken).pipe(
      map((result: any) => {
        if (this.isBrowser) {
          sessionStorage.setItem(this.getRegTokenExpiryName(), this.getTimestampFromSeconds(result?.expires_in));
          sessionStorage.setItem(this.getRegTokenName(), result?.access_token);
        }
        return result?.access_token;
      })
    );
  }

  private getRegTokenRequest(authToken: string) {
    const uuid = uuidv4();
    const url = `${this.config.apigeeApi?.baseUrl}${this.config.compaxAngularApi.compaxToken.registerTokenURl}`;

    let body = {};
    if (
      this.oauthService.getIdToken() !== null &&
      this.oauthService.getAccessToken() !== null &&
      this.oauthService.hasValidAccessToken()
    ) {
      body = JSON.stringify({
        accessToken: this.oauthService.getAccessToken(),
        idToken: this.oauthService.getIdToken(),
        keepSessionId: true,
      });
    }

    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${authToken}`)
      .set('MWH-CompaxToken', `Bearer ${authToken}`)
      .set('Content-Type', 'application/json')
      .set('DAPI-AppID', this.config?.apigeeApi?.dapiAppId)
      .set('DAPI-ClientSecret', this.config?.apigeeApi?.dapiClientSecret)
      .set('DAPI-ChannelID', this.config?.apigeeApi?.dapiChannelId)
      .set('DAPI-RequestID', uuid)
      .set('DAPI-CorrelationID', uuid)
      .set('DAPI-RequestTimestamp', new Date().toISOString());

    return this.http.post(url, body, { headers });
  }

  private getTimestampFromSeconds(seconds): string {
    const expSec = seconds;
    const expDate = new Date();
    const expiryH = Math.floor(expSec / 3600);
    const expiryM = Math.floor((expSec % 3600) / 60);
    const expiryS = expSec % 60;
    expDate.setHours(expDate.getHours() + expiryH);
    expDate.setMinutes(expDate.getMinutes() + expiryM);
    expDate.setSeconds(expDate.getSeconds() + expiryS);
    return expDate.getTime().toString();
  }

  protected getAuthTokenName() {
    return 'vmo2_sess_identified_ID';
  }

  protected getAuthTokenExpiryName() {
    return 'vmo2_sess_identified_ID_expiry';
  }

  protected getRegTokenName() {
    return 'vmo2_sess_auth_ID';
  }

  protected getRegTokenExpiryName() {
    return 'vmo2_sess_auth_ID_expiry';
  }
}
